import {Component, OnInit, Input} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import {AuthenticationService} from '@app/auth';
import { IInputProps } from '@shared/components/input/input.component';
import { ITextAreaProps } from '@shared/models/shared.models';
import {VendorIntegrationService} from '@shared/services/vendor-integration.service';
import {environment} from '@env/environment';
import {IUserProfile} from '@app/store/Models/settings/settingsModel';
import {selectUserProfile} from '@app/store/Selectors/workspace/workspace.selectors';
import {Store} from '@ngrx/store';
import {tap, catchError} from 'rxjs/operators';
import {saveUserProfile} from '@app/store/Actions/settings/settings.actions';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {of} from 'rxjs';
import {DialogService} from '@shared/services/dialog/dialog.service';

@UntilDestroy()
@Component({
  selector: 'app-social-user-request-modal',
  templateUrl: './social-user-request-modal.component.html',
  styleUrl: './social-user-request-modal.component.scss'
})
export class SocialUserRequestModalComponent implements OnInit {
  @Input() reportTitle: string;
  requestAccessFormModal: UntypedFormGroup;

  inputProps: { [key: string]: IInputProps } = {
    email: {
      class: 'form-control uui-modal-input input-error',
      placeholder: 'Corporate Email',
      label: 'Corporate Email *',
    },
    firstName: {
      class: 'form-control uui-modal-input input-error',
      placeholder: 'First Name',
      label: 'First Name *',
      disabled: true,
    },
    lastName: {
      class: 'form-control uui-modal-input',
      placeholder: 'Last Name',
      label: 'Last Name *',
      disabled: true,
    },
    phoneNumber: {
      class: 'form-control uui-modal-input',
      placeholder: 'Phone Number',
      label: 'Phone Number',
    },
  };

  inputCommentProps: ITextAreaProps = {
    name: 'topic',
    class: 'form-control uui-modal-input',
    label: 'Optional message',
    rows: 2,
    cols: 50,
    minLength: 1,
    maxLength: 500,
  };

  private readonly userProfile$ = this.store.select(selectUserProfile);

  constructor(
    public modal: NgbActiveModal,
    private authService: AuthenticationService,
    private vendorIntegrationService: VendorIntegrationService,
    private store: Store,
    private dialogService: DialogService,
  ) {}

  ngOnInit() {
    this.requestAccessFormModal = new UntypedFormGroup({
      FirstName: new UntypedFormControl('', Validators.required),
      LastName: new UntypedFormControl('', Validators.required),
      Email: new UntypedFormControl('', [Validators.required, Validators.email]),
      PhoneNumber: new UntypedFormControl('', [
        Validators.required,
        Validators.pattern(/^\d{10}$/),
      ]),
      Comment: new UntypedFormControl(''),
    });
    this.authService.getProfile().subscribe((profile ) => {
      this.setFieldValue('FirstName', profile.firstName, '*J', this.inputProps.firstName);
      this.setFieldValue('LastName', profile.lastName, '*Doe', this.inputProps.lastName);
    });
  }

  updateControlValidity(controlName: string) {
    const control = this.requestAccessFormModal.controls[controlName];
    control.updateValueAndValidity();
    control.markAsTouched();

    const inputProps = this.inputProps[controlName.toLowerCase()];
    if (inputProps) {
      inputProps.label = inputProps.label.replace('*', '').replace('is required', '').trim();
    }
  }

  onModalClose() {
    this.modal.close();
  }

  dismissModal() {
    this.modal.dismiss('close');
  }

  handleSendInquiry() {
    this.requestAccessFormModal.updateValueAndValidity();
    if (!this.requestAccessFormModal.valid) return;

    this.userProfile$
      .pipe(
        tap(userProfile => this.updateUserProfile(userProfile)),
        catchError(() => {
          this.openErrorModal();
          return of(null);
        }),
        untilDestroyed(this),
      )
      .subscribe(userProfile => {
        if (userProfile) {
          this.dismissModal();
          this.showSuccessModal();
          this.submitInquiryForm(userProfile);
        }
      }, () => {
        this.openErrorModal();
      });
  }

  submitInquiryForm(profile: IUserProfile) {
    const body = {
      username: profile.email,
      phone: profile.phone || this.requestAccessFormModal.get('PhoneNumber').value,
      corporateemail: this.requestAccessFormModal.get('Email').value,
      firstname: profile.firstName,
      lastname: profile.lastName,
      comment: this.requestAccessFormModal.get('Comment').value,
      requestedItem: this.reportTitle,
    };
    this.vendorIntegrationService
      .submitPardotForm(environment.pardotForms.socialEmailInquiryForm, body)
      .subscribe(() => this.onModalClose(), () => {
        this.onModalClose();
        this.openErrorModal();
      });
  }

  setFieldValue(field: string, value: string, invalidValue: string, inputProps: IInputProps): void {
    const formField = this.requestAccessFormModal.get(field);
    if (value !== invalidValue) {
      formField?.setValue(value);
    } else {
      inputProps.disabled = false;
    }
  }

  private updateUserProfile(userProfile: IUserProfile) {
    const updatedProfile = {
      ...userProfile,
      firstName: this.requestAccessFormModal.get('FirstName').value,
      lastName: this.requestAccessFormModal.get('LastName').value,
      phone: this.requestAccessFormModal.get('PhoneNumber').value,
    };
    return this.store.dispatch(saveUserProfile({ userProfile: updatedProfile }));
  }

  private showSuccessModal() {
    const modalServiceRef = this.dialogService.openGeneric({
      title: 'Entitlement Request sent!',
      body: 'Our team has received your request and will process your inquiry shortly. We are looking forward to helping you!',
      closeButtonLabel: 'Done',
    });
    modalServiceRef.result.then(() => {});
  }

  private openErrorModal() {
    this.dialogService.openError().result.then();
  }
}
